import { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { GlobalStyles } from '@/styles/GlobalStyles';
import { getTheme } from '@/styles/theme';
import { store } from '@/Redux/reduxStore';
import { router, preloadComponents } from '@/Services/RouteContext';
import { LoaderOverlay } from '@/components/common/LoaderOverlay';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ThemeContextProvider, useThemeContext } from '@/contexts/ThemeContext';
import { TraceContextProvider, useTraceContext } from '@/contexts/TraceContext';
import { initializeDynatraceLogger, setTraceId } from '@/Services/dynatrace-logger';
import { setupGlobalErrorHandling } from '@/Services/dynatrace-error-handler';
import { PostMessageAuthInitializer } from '@/lib/auth/PostMessageAuthInitializer';
import { resetWithdrawalDataState } from '@/hooks/useWithdrawalData';
import { redirectDirectAccessToIdentity } from '@/Utils/direct-access-guard';

const shouldMountApp = !redirectDirectAccessToIdentity();

// Reset withdrawal data state on app initialization
resetWithdrawalDataState();

// Initialize Dynatrace Logger
initializeDynatraceLogger({
  enabled: import.meta.env.VITE_DYNATRACE_ENABLED === 'true',
  url: import.meta.env.VITE_DYNATRACE_URL || '',
  apiToken: import.meta.env.VITE_DYNATRACE_API_TOKEN || '',
  serviceName: import.meta.env.VITE_DYNATRACE_SERVICE_NAME || 'WithdrawalsApp',
  ingestEndpoint: import.meta.env.VITE_DYNATRACE_INGEST_ENDPOINT || '/api/v2/logs/ingest',
  batchSizeLimit: parseInt(import.meta.env.VITE_DYNATRACE_BATCH_SIZE_LIMIT || '10', 10),
  period: parseInt(import.meta.env.VITE_DYNATRACE_PERIOD || '2', 10),
  queueSizeLimit: parseInt(import.meta.env.VITE_DYNATRACE_QUEUE_SIZE_LIMIT || '10000', 10),
  tags: import.meta.env.VITE_DYNATRACE_TAGS || '',
  minimumLevel: import.meta.env.VITE_DYNATRACE_MINIMUM_LEVEL as any || 'INFO',
});

// Setup global error handling
setupGlobalErrorHandling();

const ThemedApp = () => {
  const { mode, brand } = useThemeContext();
  const { traceId } = useTraceContext();
  const currentTheme = getTheme(mode, brand);

  // Sync trace ID into the singleton so APIs and logger can access it
  setTraceId(traceId);

  // Preload components in the background after initial render
  useEffect(() => {
    preloadComponents();
  }, []);

  return (
    <ThemeProvider theme={currentTheme}>
      <>
        <PostMessageAuthInitializer />
        <GlobalStyles theme={currentTheme} />
        <ErrorBoundary>
          <Suspense fallback={null}>
            <LoaderOverlay />
            <RouterProvider router={router} />
          </Suspense>
        </ErrorBoundary>
      </>
    </ThemeProvider>
  );
};

if (shouldMountApp) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <Provider store={store}>
      <TraceContextProvider>
        <ThemeContextProvider>
          <ThemedApp />
        </ThemeContextProvider>
      </TraceContextProvider>
    </Provider>
  );
}
